/**
 * Lazy loader for the heavy half of a template.
 *
 * Most of the library is `instructions` + `persona` — several hundred words of
 * markdown per role that are needed exactly once, when a user picks a card, and
 * never during browsing. The gallery renders only the card fields (title,
 * summary, domain, group, keywords, automation ids).
 *
 * So the data ships split:
 *   - `agent-templates/index.json` — card fields for every role, statically
 *     imported by `constants.ts` because the gallery can't render without it.
 *   - `agent-templates/*.identity.json` — `{[id]: {instructions, persona}}` per
 *     domain, dynamically imported here. Vite emits each as its own chunk, so
 *     opening the gallery costs the index alone and a click pulls in only the
 *     domain the picked role belongs to.
 */

import type { IAgentTemplate, ITemplateCard } from './constants';

/** The `{instructions, persona}` pair stored per template id in a chunk. */
interface ITemplateIdentity {
  instructions: string;
  persona: string;
}

/**
 * Dynamic importers keyed by domain. Written as explicit literal imports (not a
 * computed path) so Vite can statically see each chunk at build time — a
 * template-literal import path would defeat code splitting or pull in every
 * file. Add a line here when a new domain ships.
 */
const IDENTITY_CHUNKS: Record<
  string,
  () => Promise<{ default: Record<string, ITemplateIdentity> }>
> = {
  finance: () => import('./agent-templates/finance.identity.json'),
  banking: () => import('./agent-templates/banking.identity.json'),
};

/**
 * Chunks already fetched, keyed by domain. The browser caches the module anyway, but
 * this also de-dupes concurrent requests: two quick clicks in the same domain
 * await one in-flight promise instead of racing two imports.
 */
const chunkCache = new Map<
  string,
  Promise<Record<string, ITemplateIdentity>>
>();

function loadChunk(domain: string): Promise<Record<string, ITemplateIdentity>> {
  const cached = chunkCache.get(domain);
  if (cached) return cached;
  const importer = IDENTITY_CHUNKS[domain];
  if (!importer) {
    return Promise.reject(
      new Error(`No identity chunk registered for domain "${domain}"`),
    );
  }
  const promise = importer().then(mod => mod.default);
  // Drop a failed fetch so a later click can retry rather than replaying the
  // rejection forever.
  promise.catch(() => chunkCache.delete(domain));
  chunkCache.set(domain, promise);
  return promise;
}

/**
 * Resolve a gallery card into the full template, fetching its domain's
 * identity chunk on demand.
 *
 * Throws if the card's id is absent from its chunk — that means the index and
 * the chunks were generated from different data, and seeding an agent with empty
 * instructions would be worse than failing loudly.
 */
export async function loadAgentTemplate(
  card: ITemplateCard,
): Promise<IAgentTemplate> {
  const chunk = await loadChunk(card.domain);
  const identity = chunk[card.id];
  if (!identity) {
    throw new Error(`Template "${card.id}" is missing from its identity chunk`);
  }
  return { ...card, ...identity };
}

/**
 * Warm a domain's chunk without blocking on it. Called on card hover/focus so
 * the fetch is usually already done by the time a click needs it. Failures are
 * swallowed — this is a prefetch, and `loadAgentTemplate` will surface a real
 * error if the click actually needs the chunk and it can't be had.
 */
export function prefetchTemplateIdentity(domain: string): void {
  if (!IDENTITY_CHUNKS[domain] || chunkCache.has(domain)) return;
  void loadChunk(domain).catch(() => {});
}
